import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

const TeamCapacity = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('current-week');
  const [capacityData, setCapacityData] = useState({});
  
  // Site opening hours and settings for advanced capacity calculation
  const [siteSettings, setSiteSettings] = useState({
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    start_time: '08:00',
    end_time: '17:00',
    daily_break_duration: 1,
    bank_holidays_enabled: false,
    company_closures_enabled: false,
    bank_holidays: [],
    company_closures: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (users.length > 0 && tasks.length > 0) {
      calculateCapacity();
    }
  }, [users, tasks, selectedSite, selectedTimeframe]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const [usersRes, sitesRes, tasksRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/sites`, { headers }),
        axios.get(`${API}/admin/tasks`, { headers })
      ]);

      setUsers(usersRes.data || []);
      setSites(sitesRes.data || []);
      setTasks(tasksRes.data || []);
      
      // Try to fetch team settings, but handle if endpoint doesn't exist
      try {
        const settingsRes = await axios.get(`${API}/admin/organisation-settings`, { headers });
        if (settingsRes.data) {
          setSiteSettings({
            working_days: settingsRes.data.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            start_time: settingsRes.data.start_time || '08:00',
            end_time: settingsRes.data.end_time || '17:00',
            daily_break_duration: settingsRes.data.daily_break_duration || 1,
            bank_holidays_enabled: settingsRes.data.bank_holidays_enabled || false,
            company_closures_enabled: settingsRes.data.company_closures_enabled || false,
            bank_holidays: [],
            company_closures: settingsRes.data.custom_holidays || []
          });
          
          if (settingsRes.data.bank_holidays_enabled) {
            await fetchBankHolidays();
          }
        }
      } catch (settingsError) {
        console.warn('Organisation settings endpoint not available, using defaults:', settingsError);
        setSiteSettings({
          working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          start_time: '08:00',
          end_time: '17:00',
          daily_break_duration: 1,
          bank_holidays_enabled: false,
          company_closures_enabled: false,
          bank_holidays: [],
          company_closures: []
        });
      }
      
    } catch (error) {
      console.error('Failed to fetch team capacity data:', error);
      setUsers([]);
      setSites([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankHolidays = async () => {
    try {
      const response = await fetch('https://www.gov.uk/bank-holidays.json');
      const data = await response.json();
      const currentYear = new Date().getFullYear();
      const englandWales = data['england-and-wales'].events.filter(event => 
        new Date(event.date).getFullYear() === currentYear
      );
      
      setSiteSettings(prev => ({
        ...prev,
        bank_holidays: englandWales
      }));
    } catch (error) {
      console.error('Failed to fetch bank holidays:', error);
    }
  };

  const getTimeframeRange = () => {
    const now = new Date();
    
    switch (selectedTimeframe) {
      case 'current-week':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return { start: startOfWeek, end: endOfWeek };
      
      case 'next-week':
        const nextWeekStart = new Date(now);
        nextWeekStart.setDate(now.getDate() - now.getDay() + 8);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        return { start: nextWeekStart, end: nextWeekEnd };
      
      case 'current-month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start: monthStart, end: monthEnd };
      
      case 'next-month':
        const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        return { start: nextMonthStart, end: nextMonthEnd };
      
      default:
        return { start: now, end: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) };
    }
  };

  const isBankHoliday = (date) => {
    if (!siteSettings.bank_holidays_enabled) return false;
    const dateStr = date.toISOString().split('T')[0];
    return siteSettings.bank_holidays.some(holiday => holiday.date === dateStr);
  };

  const isCompanyClosure = (date) => {
    if (!siteSettings.company_closures_enabled) return false;
    const dateStr = date.toISOString().split('T')[0];
    return siteSettings.company_closures.some(closure => {
      const startDate = new Date(closure.start_date);
      const endDate = new Date(closure.end_date);
      return date >= startDate && date <= endDate;
    });
  };

  const getDayName = (date) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const calculateTimeOverlap = (userStart, userEnd, siteStart, siteEnd) => {
    const overlapStart = Math.max(userStart, siteStart);
    const overlapEnd = Math.min(userEnd, siteEnd);
    return Math.max(0, overlapEnd - overlapStart);
  };

  const calculateMemberAvailableCapacity = (user) => {
    const { start, end } = getTimeframeRange();
    let totalAvailableMinutes = 0;
    
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dayName = getDayName(currentDate);
      
      const userWorkingDays = user.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      if (!userWorkingDays.includes(dayName)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      if (!siteSettings.working_days.includes(dayName)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      if (isBankHoliday(currentDate)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      if (isCompanyClosure(currentDate)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      const userStartMinutes = timeToMinutes(user.start_time || '07:00');
      const userEndMinutes = timeToMinutes(user.end_time || '18:00');
      const siteStartMinutes = timeToMinutes(siteSettings.start_time);
      const siteEndMinutes = timeToMinutes(siteSettings.end_time);
      
      const overlapMinutes = calculateTimeOverlap(userStartMinutes, userEndMinutes, siteStartMinutes, siteEndMinutes);
      
      // Daily break in minutes (rounded — the stored value is decimal hours and
      // a value like 35 min is 0.583333… so Math.round avoids 7-second drift).
      const dailyBreakMinutes = Math.round((siteSettings.daily_break_duration || 0) * 60);
      const availableDailyMinutes = Math.max(0, overlapMinutes - dailyBreakMinutes);
      
      totalAvailableMinutes += availableDailyMinutes;
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return totalAvailableMinutes / 60;
  };

  const calculateMemberWorkload = (user) => {
    const { start, end } = getTimeframeRange();
    
    const userTasks = tasks.filter(task => {
      if (!task.assigned_to || !task.assigned_to.includes(user.id)) return false;
      if (!task.due_date) return false;
      
      const dueDate = new Date(task.due_date);
      return dueDate >= start && dueDate <= end;
    });
    
    let totalWorkload = 0;
    userTasks.forEach(task => {
      const assigneeCount = task.assigned_to ? task.assigned_to.length : 1;
      const individualWorkload = (task.task_capacity || 1.0) / assigneeCount;
      totalWorkload += individualWorkload;
    });
    
    const siteStartMinutes = timeToMinutes(siteSettings.start_time);
    const siteEndMinutes = timeToMinutes(siteSettings.end_time);
    const siteDailyMinutes = siteEndMinutes - siteStartMinutes;
    const dailyBreakMinutes = Math.round((siteSettings.daily_break_duration || 0) * 60);
    const workingHoursPerDay = (siteDailyMinutes - dailyBreakMinutes) / 60;
    
    return totalWorkload * workingHoursPerDay;
  };

  const calculateCapacity = async () => {
    try {
      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        ...(selectedSite !== 'all' && { site_id: selectedSite })
      });

      const response = await axios.get(`${API}/admin/team-capacity-enhanced?${params}`);
      const data = response.data;

      const transformedCapacity = {};
      
      data.capacity_data.forEach(userCapacity => {
        transformedCapacity[userCapacity.user_id] = {
          user: {
            id: userCapacity.user_id,
            username: userCapacity.username,
            full_name: userCapacity.full_name,
            profile_photo_url: userCapacity.profile_photo_url,
            role: userCapacity.role,
            department: userCapacity.department,
            assigned_sites: userCapacity.assigned_sites,
            working_days: userCapacity.working_days,
            start_time: userCapacity.start_time,
            end_time: userCapacity.end_time
          },
          totalTasks: userCapacity.task_count,
          totalWorkload: userCapacity.total_task_workload,
          availableWorkHours: userCapacity.available_work_hours,
          workloadPercentage: userCapacity.workload_percentage,
          completedTasks: userCapacity.completed_tasks,
          completedWorkload: userCapacity.task_details
            .filter(task => task.status === 'completed')
            .reduce((sum, task) => sum + (task.user_workload || 0), 0),
          inProgressTasks: userCapacity.in_progress_tasks,
          inProgressWorkload: userCapacity.task_details
            .filter(task => task.status === 'in_progress')
            .reduce((sum, task) => sum + (task.user_workload || 0), 0),
          pendingTasks: userCapacity.pending_tasks,
          pendingWorkload: userCapacity.task_details
            .filter(task => task.status === 'pending')
            .reduce((sum, task) => sum + (task.user_workload || 0), 0),
          utilizationRate: userCapacity.total_task_workload > 0 
            ? (userCapacity.task_details
                .filter(task => task.status === 'completed')
                .reduce((sum, task) => sum + (task.user_workload || 0), 0) / userCapacity.total_task_workload) * 100 
            : 0,
          tasks: userCapacity.task_details || [],
          enhancedData: {
            summary: data.summary,
            timeframe_info: {
              start_date: data.start_date,
              end_date: data.end_date
            }
          }
        };
      });

      setCapacityData(transformedCapacity);
      
    } catch (error) {
      console.error('Error with enhanced API, falling back to original calculation:', error);
      
      const filteredUsers = selectedSite === 'all' 
        ? users 
        : users.filter(user => user.assigned_sites?.includes(selectedSite));

      const filteredTasks = selectedSite === 'all'
        ? tasks
        : tasks.filter(task => task.site_id === selectedSite);

      const timeframeTasks = filteredTasks.filter(task => {
        if (!task.due_date) return false;
        
        const dueDate = new Date(task.due_date);
        const { start, end } = getTimeframeRange();
        return dueDate >= start && dueDate <= end;
      });

      const capacity = {};
      
      filteredUsers.forEach(user => {
        const availableWorkHours = calculateMemberAvailableCapacity(user);
        const totalWorkloadHours = calculateMemberWorkload(user);
        const workloadPercentage = availableWorkHours > 0 ? (totalWorkloadHours / availableWorkHours) * 100 : 0;
        
        const userTasks = timeframeTasks.filter(task => 
          task.assigned_to && task.assigned_to.includes(user.id)
        );
        
        const completedTasks = userTasks.filter(task => task.status === 'completed');
        const inProgressTasks = userTasks.filter(task => task.status === 'in_progress');
        const pendingTasks = userTasks.filter(task => task.status === 'pending');
        
        const completedWorkload = completedTasks.reduce((sum, task) => {
          const assigneeCount = task.assigned_to ? task.assigned_to.length : 1;
          return sum + ((task.task_capacity || 1.0) / assigneeCount);
        }, 0);
        
        const inProgressWorkload = inProgressTasks.reduce((sum, task) => {
          const assigneeCount = task.assigned_to ? task.assigned_to.length : 1;
          return sum + ((task.task_capacity || 1.0) / assigneeCount);
        }, 0);
        
        const pendingWorkload = pendingTasks.reduce((sum, task) => {
          const assigneeCount = task.assigned_to ? task.assigned_to.length : 1;
          return sum + ((task.task_capacity || 1.0) / assigneeCount);
        }, 0);

        capacity[user.id] = {
          user,
          totalTasks: userTasks.length,
          totalWorkload: totalWorkloadHours / 8,
          availableWorkHours,
          workloadPercentage,
          completedTasks: completedTasks.length,
          completedWorkload,
          inProgressTasks: inProgressTasks.length,
          inProgressWorkload,
          pendingTasks: pendingTasks.length,
          pendingWorkload,
          utilizationRate: (completedWorkload + inProgressWorkload) > 0 ? (completedWorkload / (completedWorkload + inProgressWorkload)) * 100 : 0,
          tasks: userTasks
        };
      });

      setCapacityData(capacity);
    }
  };

  const getWorkloadColor = (percentage) => {
    if (percentage === 0) return 'text-gray-300 bg-slate-600';
    if (percentage <= 50) return 'text-green-300 bg-green-500/20 border border-green-500/30';
    if (percentage <= 75) return 'text-yellow-300 bg-yellow-500/20 border border-yellow-500/30';
    if (percentage <= 100) return 'text-orange-300 bg-orange-500/20 border border-orange-500/30';
    return 'text-red-300 bg-red-500/20 border border-red-500/30';
  };

  const getWorkloadStatus = (percentage) => {
    if (percentage === 0) return 'Available';
    if (percentage <= 50) return 'Light Load';
    if (percentage <= 75) return 'Moderate Load';
    if (percentage <= 100) return 'Full Load';
    return 'Overloaded';
  };

  const getTimeframeLabel = () => {
    switch (selectedTimeframe) {
      case 'current-week': return 'This Week';
      case 'next-week': return 'Next Week';
      case 'current-month': return 'This Month';
      case 'next-month': return 'Next Month';
      default: return 'All Time';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading team capacity data...</p>
          </div>
        </div>
      </div>
    );
  }

  const totalUsers = Object.keys(capacityData).length;
  const totalTasks = Object.values(capacityData).reduce((sum, data) => sum + data.totalTasks, 0);
  const totalWorkload = Object.values(capacityData).reduce((sum, data) => sum + data.totalWorkload, 0);
  const totalAvailableHours = Object.values(capacityData).reduce((sum, data) => sum + (data.availableWorkHours || 0), 0);
  const averageWorkloadPercentage = totalUsers > 0 ? Object.values(capacityData).reduce((sum, data) => sum + (data.workloadPercentage || 0), 0) / totalUsers : 0;
  const overloadedUsers = Object.values(capacityData).filter(data => (data.workloadPercentage || 0) > 100).length;

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Modern Header - Purple Gradient */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Team Capacity</h1>
        <p className="text-purple-200">Monitor team workload distribution and task completion rates</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Site Filter</label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all" className="bg-slate-900">All Sites</option>
              {sites.map(site => (
                <option key={site.id} value={site.id} className="bg-slate-900">{site.site_name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Time Period</label>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="current-week" className="bg-slate-900">This Week</option>
              <option value="next-week" className="bg-slate-900">Next Week</option>
              <option value="current-month" className="bg-slate-900">This Month</option>
              <option value="next-month" className="bg-slate-900">Next Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Team Members</p>
              <p className="text-xl font-bold text-white">{totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Total Tasks</p>
              <p className="text-xl font-bold text-white">{totalTasks}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Task Hours</p>
              <p className="text-xl font-bold text-white">{totalWorkload.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Available Hours</p>
              <p className="text-xl font-bold text-white">{totalAvailableHours.toFixed(0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Avg. Workload</p>
              <p className="text-xl font-bold text-white">{averageWorkloadPercentage.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-xs font-medium text-gray-400">Overloaded</p>
              <p className="text-xl font-bold text-white">{overloadedUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Capacity Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 bg-slate-700">
          <h2 className="text-lg font-semibold text-white">
            Team Capacity Details - {getTimeframeLabel()}
          </h2>
        </div>

        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full">
            <thead className="bg-slate-700 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Team Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Working Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Workload vs Availability
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Tasks
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Workload %
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider bg-slate-700">
                  Status Distribution
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.values(capacityData).map((data, index) => (
                <tr key={data.user.id} className={`hover:bg-slate-700/50 transition-colors ${index !== Object.values(capacityData).length - 1 ? "border-b border-slate-700/50" : ""}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {data.user.profile_photo_url ? (
                        <img
                          src={data.user.profile_photo_url}
                          alt={data.user.full_name}
                          className="w-10 h-10 rounded-full object-cover mr-3 ring-2 ring-purple-500/40"
                          data-testid={`team-capacity-avatar-${data.user.id}`}
                        />
                      ) : (
                        <div
                          className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-medium mr-3"
                          data-testid={`team-capacity-avatar-fallback-${data.user.id}`}
                        >
                          {data.user.full_name.split(' ').map(n => n[0]).join('')}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-white">{data.user.full_name}</div>
                        <div className="text-sm text-gray-400">{data.user.role} • {data.user.department}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {data.user.working_days ? data.user.working_days.length : 5} days/week
                    </div>
                    <div className="text-xs text-gray-400">
                      {data.user.start_time || '08:00'} - {data.user.end_time || '17:00'}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      <span className="font-medium">{data.totalWorkload.toFixed(1)}h</span> task workload
                    </div>
                    <div className="text-xs text-gray-400">
                      {(data.availableWorkHours || 0).toFixed(1)}h available
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{data.totalTasks} tasks</div>
                    <div className="text-xs text-gray-400">
                      {data.completedTasks}C • {data.inProgressTasks}P • {data.pendingTasks}N
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWorkloadColor(data.workloadPercentage || 0)}`}>
                        {(data.workloadPercentage || 0).toFixed(1)}%
                      </span>
                      <div className="ml-2 text-xs text-gray-400">
                        {getWorkloadStatus(data.workloadPercentage || 0)}
                      </div>
                    </div>
                    <div className="mt-1 w-full bg-slate-600 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full ${
                          (data.workloadPercentage || 0) <= 50 ? 'bg-green-500' :
                          (data.workloadPercentage || 0) <= 75 ? 'bg-yellow-500' :
                          (data.workloadPercentage || 0) <= 100 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((data.workloadPercentage || 0), 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      {data.completedTasks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                          {data.completedTasks} ✓
                        </span>
                      )}
                      {data.inProgressTasks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          {data.inProgressTasks} ⏳
                        </span>
                      )}
                      {data.pendingTasks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                          {data.pendingTasks} ⏸
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {Object.keys(capacityData).length === 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
          <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No Team Data</h3>
          <p className="text-gray-400">No team members found for the selected criteria.</p>
        </div>
      )}
    </div>
  );
};

export default TeamCapacity;
